import { action, observable } from 'mobx';
import { Container, Inject } from 'typescript-ioc';
import { IntlShape, MessageDescriptor } from 'react-intl';
import {
    HubConfigurationSettingsType,
    IEventHubsDAO,
    IHubRegistrationDTO,
} from '../../../../../../../api/event-hubs.dao';
import { EventHubsDAO } from '../../../../../../../api/implementations/event-hubs.dao';
import {
    IHubRegistrationConfigurationDAO,
    IHubRegistrationField,
    IHubRegistrationFieldConfiguration,
    IHubRegistrationFormDTO,
    IHubCustomField,
    HubCustomFieldType,
} from '../../../../../../../api/hub-registration-configuration.dao';
import { HubRegistrationConfigurationDAO } from '../../../../../../../api/implementations/hub-registration-configuration.dao';
import { IReactIntlProvider } from '../../../../../../../infrastructure/react-intl-provider';
import { IErrorParsing } from '../../../../../../../infrastructure/error-parsing';
import { alertServerError } from '../../../../../../global/modal/alerts-host/alert-templates';
import {
    LONG_RICH_TEXT_LENGTH,
    MAX_CUSTOM_FIELD_OPTIONS_COUNT,
    SHORT_PLAIN_TEXT_LENGTH,
    SHORT_RICH_TEXT_LENGTH,
} from '../../../../../../../constants/common.constants';
import {
    FIELD_IS_REQUIRED_ERROR_MESSAGE,
    MAX_LENGTH_NOT_REQUIRED_ERROR_MESSAGE,
    MAX_LONG_RICH_TEXT_LENGTH_ERROR_MESSAGE,
    MAX_OPTION_LENGTH_ERROR_MESSAGE,
    MAX_OPTIONS_COUNT_ERROR_MESSAGE,
    MAX_SHORT_RICH_TEXT_LENGTH_ERROR_MESSAGE,
    REQUIRED_ERROR_MESSAGE,
} from '../../../../../../../constants/validation-errors';
import { calcByteCodeSize } from '../../../../../../../infrastructure/utils/common.utils';
import { EVENT_HUB_TRACKING_EVENTS } from '../../../../../../../tracking-service/tracking-values';
import TrackingServiceProvider from '../../../../../../../tracking-service/tracking-service-provider';
import {
    transpileClassesToInlineStyles,
    transpileInlineStylesToClasses,
} from '../../../../../../global/rich-text-editor/utils/transpiler';
import { HubPreviewRefreshCallback } from '../../../../_content/_hub-embed/hub-preview-sync.types';
import { buildRegistrationPreviewExpectation } from '../../../../_content/_hub-embed/hub-preview-sync.expectations';
import {
    HUB_CUSTOM_FIELD_ID_ALREADY_EXISTS_TEXT,
    HUB_FIELD_ID_TEXT,
    HUB_FIELD_LABEL_TEXT,
    HUB_FIELD_OPTIONS_TEXT,
    HUB_FIELD_TYPE_TEXT,
} from './hub-registration-settings.text';

export const DEFAULT_REGISTER_BUTTON_LABEL = 'Register';
export const DEFAULT_REGISTRATION_SUBMITTED_MESSAGE =
    'Thank you for registering. Please check your email for details of how to sign in to the Hub.';

const isRichTextEmpty = (value: string): boolean =>
    !value.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
let nextTemporaryCustomFieldId = -1;
const generateTemporaryCustomFieldId = (): number => nextTemporaryCustomFieldId--;

const DEFAULT_FIELD_CONFIGURATION: IHubRegistrationFieldConfiguration = {
    formName: '',
    mandatory: false,
    visible: false,
    order: 0,
};

const cloneFields = (fields: IHubRegistrationField[]): IHubRegistrationField[] =>
    fields.map(field => ({
        ...field,
        configurations: field.configurations.map(configuration => ({ ...configuration })),
    }));

const getFieldConfiguration = (field: IHubRegistrationField): IHubRegistrationFieldConfiguration =>
    field.configurations[0] ?? DEFAULT_FIELD_CONFIGURATION;

const ensureFieldConfiguration = (field: IHubRegistrationField): IHubRegistrationFieldConfiguration => {
    if (!field.configurations.length) {
        field.configurations.push({ ...DEFAULT_FIELD_CONFIGURATION });
    }

    return field.configurations[0];
};

const cloneCustomFields = (customFields: IHubCustomField[]): IHubCustomField[] =>
    customFields.map(field => ({
        ...field,
        configurations: field.configurations.map(configuration => ({ ...configuration })),
        optionsList: field.optionsList ? [...field.optionsList] : field.optionsList,
    }));

    const dedupeCustomFieldsById = (customFields: IHubCustomField[]): IHubCustomField[] => {
    const byId = new Map<number, IHubCustomField>();
    customFields.forEach(field => byId.set(field.customFieldId, field));

    return Array.from(byId.values());
};

const getCustomFieldConfiguration = (field: IHubCustomField): IHubRegistrationFieldConfiguration =>
    field.configurations[0] ?? DEFAULT_FIELD_CONFIGURATION;

const ensureCustomFieldConfiguration = (field: IHubCustomField): IHubRegistrationFieldConfiguration => {
    if (!field.configurations.length) {
        field.configurations.push({ ...DEFAULT_FIELD_CONFIGURATION });
    }

    return field.configurations[0];
};

export type HubOrderedFieldEntry =
    | { kind: 'default'; field: IHubRegistrationField }
    | { kind: 'custom'; field: IHubCustomField };

const getEntryOrder = (entry: HubOrderedFieldEntry): number =>
    entry.kind === 'default' ? getFieldConfiguration(entry.field).order : getCustomFieldConfiguration(entry.field).order;

const isEntryVisible = (entry: HubOrderedFieldEntry): boolean =>
    entry.kind === 'default' ? getFieldConfiguration(entry.field).visible : getCustomFieldConfiguration(entry.field).visible;

const setEntryOrder = (entry: HubOrderedFieldEntry, order: number): void => {
    if (entry.kind === 'default') {
        ensureFieldConfiguration(entry.field).order = order;
    } else {
        ensureCustomFieldConfiguration(entry.field).order = order;
    }
};

const toOrderedEntries = (fields: IHubRegistrationField[], customFields: IHubCustomField[]): HubOrderedFieldEntry[] => [
    ...fields.map(field => ({ kind: 'default' as const, field: field })),
    ...customFields.map(field => ({ kind: 'custom' as const, field: field })),
];

const normalizeFieldOrders = (
    fields: IHubRegistrationField[],
    customFields: IHubCustomField[]
): { fields: IHubRegistrationField[]; customFields: IHubCustomField[] } => {
    const sortedEntries = toOrderedEntries(fields, customFields).sort(
        (left, right) => getEntryOrder(left) - getEntryOrder(right)
    );
    const displayedEntries = sortedEntries.filter(isEntryVisible);
    const hiddenEntries = sortedEntries.filter(entry => !isEntryVisible(entry));

    let order = 0;
    [...displayedEntries, ...hiddenEntries].forEach(entry => setEntryOrder(entry, order++));

    const cloneEntryField = <T extends IHubRegistrationField | IHubCustomField>(field: T): T => ({
        ...field,
        configurations: field.configurations.map(configuration => ({ ...configuration })),
    });

    return {
        fields: [...displayedEntries, ...hiddenEntries]
            .filter((entry): entry is Extract<HubOrderedFieldEntry, { kind: 'default' }> => entry.kind === 'default')
            .map(entry => cloneEntryField(entry.field)),
        customFields: [...displayedEntries, ...hiddenEntries]
            .filter((entry): entry is Extract<HubOrderedFieldEntry, { kind: 'custom' }> => entry.kind === 'custom')
            .map(entry => cloneEntryField(entry.field)),
    };
};

const buildFormPayload = (fields: IHubRegistrationField[], customFields: IHubCustomField[] = []): IHubRegistrationFormDTO => {
    const defaultFieldPayloads = fields.map(field => {
        const configuration = getFieldConfiguration(field);

        return {
            type: field.type,
            defaultFieldId: field.defaultFieldId,
            defaultFieldTitle: field.defaultFieldTitle,
            label: field.label.trim(),
            triaged: field.triaged ?? false,
            configurations: [{
                formName: configuration.formName,
                mandatory: configuration.mandatory,
                visible: configuration.visible,
                order: configuration.order,
            }],
        };
    });

    const customFieldPayloads = customFields.map(field => {
        const configuration = getCustomFieldConfiguration(field);
        const isNewField = field.customFieldId < 0;

        return {
            type: field.type,
            customFieldId: isNewField ? null : field.customFieldId,
            externalFieldId: field.fieldId.trim(),
            label: field.label.trim(),
            description: field.description?.trim() || undefined,
            configurations: [{
                formName: configuration.formName,
                mandatory: configuration.mandatory,
                visible: configuration.visible,
                order: configuration.order,
            }],
            locked: field.locked,
            optionsList: field.type === HubCustomFieldType.Dropdown ? (field.optionsList ?? []) : undefined,
        };
    });

    return {
        fields: [...defaultFieldPayloads, ...customFieldPayloads] as unknown as IHubRegistrationField[],
    };
};

const isCustomFieldWireEntry = (field: unknown): boolean => {
    const record = field as Record<string, unknown>;
    return record.customFieldId !== undefined || record.externalFieldId !== undefined;
};

const splitWireFields = (
    wireFields: unknown[]
): { defaultFields: IHubRegistrationField[]; customFields: IHubCustomField[] } => {
    const defaultFields: IHubRegistrationField[] = [];
    const customFields: IHubCustomField[] = [];

    (wireFields || []).forEach(field => {
        if (isCustomFieldWireEntry(field)) {
            const record = field as Record<string, unknown>;
            const resolvedId = (record.customFieldId ?? record.id) as number | undefined;
            customFields.push({
                customFieldId: resolvedId ?? generateTemporaryCustomFieldId(),
                fieldId: (record.externalFieldId as string) ?? '',
                label: record.label as string,
                description: record.description as string | undefined,
                type: record.type as HubCustomFieldType,
                configurations: (record.configurations as IHubRegistrationFieldConfiguration[]) ?? [],
                locked: record.locked as boolean | undefined,
                optionsList: (record.optionsList as string[] | null | undefined) ?? undefined,
            });
        } else {
            defaultFields.push(field as IHubRegistrationField);
        }
    });

    return { defaultFields: defaultFields, customFields: dedupeCustomFieldsById(customFields) };
};

export class HubRegistrationSettingsVM {
    @observable
    private registrationEnabled = false;
    @observable
    private registrationApprovalEnabled = false;
    @observable
    private accountRegistrationApprovalEnabled = false;
    @observable
    private oneStepRegistrationEnabled = false;
    @observable
    private displayName = '';
    @observable
    private instructions = '';
    @observable
    private registerButtonLabel = DEFAULT_REGISTER_BUTTON_LABEL;
    @observable
    private submittedMessage = DEFAULT_REGISTRATION_SUBMITTED_MESSAGE;
    @observable
    private registerButtonLabelTouched = false;
    @observable
    private submittedMessageTouched = false;
    @observable
    private fields: IHubRegistrationField[] = [];
    @observable
    private loading = false;
    @observable
    private saving = false;
    @observable
    private hiddenFieldsExpanded = false;
    @observable
    private editingField: IHubRegistrationField | null = null;
    @observable
    private editingFieldDraft: IHubRegistrationField | null = null;
    @observable
    private editingFieldLabelError: string | null = null;
    @observable
    private customFields: IHubCustomField[] = [];
    @observable
    private editingCustomField: IHubCustomField | null = null;
    @observable
    private editingCustomFieldDraft: IHubCustomField | null = null;
    @observable
    private isNewCustomFieldDraft = false;
    @observable
    private editingCustomFieldIdError: string | null = null;
    @observable
    private editingCustomFieldLabelError: string | null = null;
    @observable
    private editingCustomFieldDescriptionError: string | null = null;
    @observable
    private editingCustomFieldTypeError: string | null = null;
    @observable
    private editingCustomFieldOptionsError: string | null = null;
    @observable
    private showDeleteConfirmation = false;
    @observable
    private fieldToDelete: IHubCustomField | null = null;

    private registrationEnabledSnapshot = false;
    private registrationApprovalEnabledSnapshot = false;
    private oneStepRegistrationEnabledSnapshot = false;
    private displayNameSnapshot = '';
    private instructionsSnapshot = '';
    private registerButtonLabelSnapshot = DEFAULT_REGISTER_BUTTON_LABEL;
    private submittedMessageSnapshot = DEFAULT_REGISTRATION_SUBMITTED_MESSAGE;
    private fieldsSnapshot: IHubRegistrationField[] = [];
    private customFieldsSnapshot: IHubCustomField[] = [];

    constructor(
        private readonly accountId: number,
        private readonly eventHubId: number,
        @Inject private readonly eventHubsDAO: IEventHubsDAO = Container.get(EventHubsDAO),
        @Inject private readonly hubRegistrationConfigurationDAO: IHubRegistrationConfigurationDAO =
            Container.get(HubRegistrationConfigurationDAO),
        @Inject private readonly intlProvider: IReactIntlProvider = Container.get(IReactIntlProvider),
        @Inject private readonly trackingService: TrackingServiceProvider = Container.get(TrackingServiceProvider),
        private readonly onHubPreviewRefresh?: HubPreviewRefreshCallback,
    ) {
        void this.loadSettings();
    }

    public get intl(): IntlShape {
        return this.intlProvider.getIntl();
    }

    public isLoading(): boolean {
        return this.loading;
    }

    public isSaving(): boolean {
        return this.saving;
    }

    public isRegistrationEnabled(): boolean {
        return this.registrationEnabled;
    }

    public isAccountRegistrationApprovalEnabled(): boolean {
        return this.accountRegistrationApprovalEnabled;
    }

    public isRegistrationApprovalEnabled(): boolean {
        return this.registrationApprovalEnabled;
    }

    public isOneStepRegistrationEnabled(): boolean {
        return this.oneStepRegistrationEnabled;
    }

    public getDisplayName(): string {
        return this.displayName;
    }

    public getInstructions(): string {
        return this.instructions;
    }

    public getRegisterButtonLabel(): string {
        return this.registerButtonLabel;
    }

    public getSubmittedMessage(): string {
        return this.submittedMessage;
    }

    public getRegisterButtonLabelError(): string | null {
        const trimmedButtonLabel = this.registerButtonLabel.trim();
        if (!trimmedButtonLabel) {
            return this.registerButtonLabelTouched ? REQUIRED_ERROR_MESSAGE : null;
        }
        if (trimmedButtonLabel.length > SHORT_PLAIN_TEXT_LENGTH) {
            return MAX_LENGTH_NOT_REQUIRED_ERROR_MESSAGE;
        }
        return null;
    }

    public getSubmittedMessageError(): string | null {
        if (isRichTextEmpty(this.submittedMessage)) {
            return this.submittedMessageTouched ? REQUIRED_ERROR_MESSAGE : null;
        }
        if (calcByteCodeSize(this.submittedMessage) > LONG_RICH_TEXT_LENGTH) {
            return MAX_LONG_RICH_TEXT_LENGTH_ERROR_MESSAGE;
        }
        return null;
    }

    public getDisplayNameError(): string | null {
        if (this.displayName.trim().length > SHORT_PLAIN_TEXT_LENGTH) {
            return MAX_LENGTH_NOT_REQUIRED_ERROR_MESSAGE;
        }
        return null;
    }

    public getInstructionsError(): string | null {
        if (calcByteCodeSize(this.instructions) > LONG_RICH_TEXT_LENGTH) {
            return MAX_LONG_RICH_TEXT_LENGTH_ERROR_MESSAGE;
        }
        return null;
    }

    public areHiddenFieldsExpanded(): boolean {
        return this.hiddenFieldsExpanded;
    }

    public getEditingField(): IHubRegistrationField | null {
        return this.editingField;
    }

    public getEditingFieldDraft(): IHubRegistrationField | null {
        return this.editingFieldDraft;
    }

    public getEditingFieldLabelError(): string | null {
        return this.editingFieldLabelError;
    }

    public getDisplayedFields(): IHubRegistrationField[] {
        return this.getOrderedFields().filter(field => getFieldConfiguration(field).visible);
    }

    public getHiddenFields(): IHubRegistrationField[] {
        return this.getOrderedFields().filter(field => !getFieldConfiguration(field).visible);
    }

    public getDisplayedCustomFields(): IHubCustomField[] {
        return this.getOrderedCustomFields().filter(field => getCustomFieldConfiguration(field).visible);
    }

    public getHiddenCustomFields(): IHubCustomField[] {
        return this.getOrderedCustomFields().filter(field => !getCustomFieldConfiguration(field).visible);
    }
    public getDisplayedOrderedFields(): HubOrderedFieldEntry[] {
        return this.getAllOrderedEntries().filter(isEntryVisible);
    }

    public getHiddenOrderedFields(): HubOrderedFieldEntry[] {
        return this.getAllOrderedEntries().filter(entry => !isEntryVisible(entry));
    }

    private getAllOrderedEntries(): HubOrderedFieldEntry[] {
        return toOrderedEntries(this.fields, this.customFields).sort(
            (left, right) => getEntryOrder(left) - getEntryOrder(right)
        );
    }

    public getEditingCustomField(): IHubCustomField | null {
        return this.editingCustomField;
    }

    public getEditingCustomFieldDraft(): IHubCustomField | null {
        return this.editingCustomFieldDraft;
    }

    public isCreatingNewCustomField(): boolean {
        return this.isNewCustomFieldDraft;
    }

    public isEditingCustomFieldVisible(): boolean {
        return this.editingCustomFieldDraft ? getCustomFieldConfiguration(this.editingCustomFieldDraft).visible : false;
    }

    public isEditingCustomFieldMandatory(): boolean {
        return this.editingCustomFieldDraft ? getCustomFieldConfiguration(this.editingCustomFieldDraft).mandatory : false;
    }

    public isRichTextLabelType(type: HubCustomFieldType): boolean {
        return type === HubCustomFieldType.Checkbox;
    }

    public getEditingCustomFieldIdError(): string | null {
        return this.editingCustomFieldIdError;
    }

    public getEditingCustomFieldLabelError(): string | null {
        return this.editingCustomFieldLabelError;
    }

    public getEditingCustomFieldDescriptionError(): string | null {
        return this.editingCustomFieldDescriptionError;
    }

    public getEditingCustomFieldTypeError(): string | null {
        return this.editingCustomFieldTypeError;
    }

    public getEditingCustomFieldOptionsError(): string | null {
        return this.editingCustomFieldOptionsError;
    }

    public getEditingCustomFieldOptionsText(): string {
        return this.editingCustomFieldDraft?.optionsList?.join('\n') ?? '';
    }

    public canAddCustomField(): boolean {
        return this.customFields.length < 25;
    }

    public hasUnsavedChanges(): boolean {
        return this.registrationEnabledSnapshot !== this.registrationEnabled
            || this.registrationApprovalEnabledSnapshot !== this.registrationApprovalEnabled
            || this.oneStepRegistrationEnabledSnapshot !== this.oneStepRegistrationEnabled
            || this.displayNameSnapshot !== this.displayName
            || this.instructionsSnapshot !== this.instructions
            || this.registerButtonLabelSnapshot !== this.registerButtonLabel
            || this.submittedMessageSnapshot !== this.submittedMessage
            || JSON.stringify(this.fieldsSnapshot) !== JSON.stringify(this.fields)
            || JSON.stringify(this.customFieldsSnapshot) !== JSON.stringify(this.customFields);
    }

    public isSaveButtonDisabled(): boolean {
        return this.loading || this.saving;
    }

    public isCancelButtonDisabled(): boolean {
        return this.loading || this.saving || !this.hasUnsavedChanges();
    }

    public canEditFieldVisibility(field: IHubRegistrationField): boolean {
        return !!field.canBeHidden;
    }

    public canEditFieldMandatory(field: IHubRegistrationField): boolean {
        return !!field.canBeOptional;
    }

    public isEditingFieldVisible(): boolean {
        return this.editingFieldDraft ? getFieldConfiguration(this.editingFieldDraft).visible : false;
    }

    public isEditingFieldMandatory(): boolean {
        return this.editingFieldDraft ? getFieldConfiguration(this.editingFieldDraft).mandatory : false;
    }

    @action
    public toggleRegistrationEnabled = (): void => {
        this.registrationEnabled = !this.registrationEnabled;

        if (!this.registrationEnabled) {
            this.oneStepRegistrationEnabled = false;
        }
    };

    @action
    public toggleOneStepRegistrationEnabled = (): void => {
        if (!this.registrationEnabled) {
            return;
        }

        this.oneStepRegistrationEnabled = !this.oneStepRegistrationEnabled;
    };

    @action
    public toggleRegistrationApproval = (): void => {
        this.registrationApprovalEnabled = !this.registrationApprovalEnabled;
    };

    @action
    public setDisplayName = (value: string): void => {
        this.displayName = value;
    };

    @action
    public setInstructions = (value: string): void => {
        this.instructions = value;
    };

    @action
    public setRegisterButtonLabel = (value: string): void => {
        this.registerButtonLabel = value;
    };

    @action
    public setSubmittedMessage = (value: string): void => {
        this.submittedMessage = value;
    };

    @action
    public markRegisterButtonLabelTouched = (): void => {
        this.registerButtonLabelTouched = true;
    };

    @action
    public markSubmittedMessageTouched = (): void => {
        this.submittedMessageTouched = true;
    };

    @action
    public toggleHiddenFieldsExpanded = (): void => {
        this.hiddenFieldsExpanded = !this.hiddenFieldsExpanded;
    };

    @action
    public openFieldEditor = (field: IHubRegistrationField): void => {
        this.editingField = field;
        this.editingFieldDraft = {
            ...field,
            configurations: field.configurations.map(configuration => ({ ...configuration })),
        };
        this.editingFieldLabelError = null;
    };

    @action
    public closeFieldEditor = (): void => {
        this.editingField = null;
        this.editingFieldDraft = null;
        this.editingFieldLabelError = null;
    };

    @action
    public setEditingFieldLabel = (label: string): void => {
        if (!this.editingFieldDraft) {
            return;
        }

        this.editingFieldDraft.label = label;
        this.editingFieldLabelError = this.validateFieldLabel(label);
    };

    @action
    public toggleEditingFieldVisibility = (): void => {
        if (!this.editingFieldDraft || !this.canEditFieldVisibility(this.editingFieldDraft)) {
            return;
        }

        const configuration = ensureFieldConfiguration(this.editingFieldDraft);
        configuration.visible = !configuration.visible;

        if (!configuration.visible) {
            configuration.mandatory = false;
        }
    };

    @action
    public toggleEditingFieldMandatory = (): void => {
        if (!this.editingFieldDraft || !this.canEditFieldMandatory(this.editingFieldDraft)) {
            return;
        }

        const configuration = ensureFieldConfiguration(this.editingFieldDraft);
        if (!configuration.visible) {
            return;
        }

        configuration.mandatory = !configuration.mandatory;
    };

    @action
    public applyFieldEditor = (): boolean => {
        if (!this.editingField || !this.editingFieldDraft) {
            return false;
        }

        const labelError = this.validateFieldLabel(this.editingFieldDraft.label);
        if (labelError) {
            this.editingFieldLabelError = labelError;
            return false;
        }

        const wasVisible = getFieldConfiguration(this.editingField).visible;
        const isVisible = getFieldConfiguration(this.editingFieldDraft).visible;

        const updatedField: IHubRegistrationField = {
            ...this.editingFieldDraft,
            label: this.editingFieldDraft.label.trim(),
            configurations: this.editingFieldDraft.configurations.map(configuration => ({ ...configuration })),
        };

        if (wasVisible !== isVisible) {
            ensureFieldConfiguration(updatedField).order = Number.MAX_SAFE_INTEGER;
        }

        this.fields = this.fields.map(field =>
            field.defaultFieldId === updatedField.defaultFieldId ? updatedField : field
        );

        if (wasVisible !== isVisible) {
            this.recalculateAllFieldOrders();
        }

        this.closeFieldEditor();
        return true;
    };

    @action
    public addNewCustomField = (): void => {
        if (!this.canAddCustomField()) {
            return;
        }

        const customFieldId = generateTemporaryCustomFieldId();
        const maxDefaultFieldOrder = this.fields.reduce(
            (max, field) => Math.max(max, getFieldConfiguration(field).order),
            -1
        );
        const maxCustomFieldOrder = this.customFields.reduce(
            (max, field) => Math.max(max, getCustomFieldConfiguration(field).order),
            -1
        );
        const maxOrder = Math.max(maxDefaultFieldOrder, maxCustomFieldOrder);
        const newField: IHubCustomField = {
            customFieldId: customFieldId,
            fieldId: '',
            label: '',
            description: '',
            type: HubCustomFieldType.Text,
            configurations: [{ ...DEFAULT_FIELD_CONFIGURATION, order: maxOrder + 1 }],
            optionsList: [],
        };

        this.editingCustomField = newField;
        this.editingCustomFieldDraft = cloneCustomFields([newField])[0];
        this.isNewCustomFieldDraft = true;
        this.editingCustomFieldIdError = null;
        this.editingCustomFieldLabelError = null;
        this.editingCustomFieldDescriptionError = null;
        this.editingCustomFieldTypeError = null;
        this.editingCustomFieldOptionsError = null;
    };

    @action
    public openCustomFieldEditor = (field: IHubCustomField): void => {
        this.editingCustomField = field;
        this.editingCustomFieldDraft = cloneCustomFields([field])[0];
        this.isNewCustomFieldDraft = false;
        this.editingCustomFieldIdError = null;
        this.editingCustomFieldLabelError = null;
        this.editingCustomFieldDescriptionError = null;
        this.editingCustomFieldTypeError = null;
        this.editingCustomFieldOptionsError = null;
    };

    @action
    public closeCustomFieldEditor = (): void => {
        this.editingCustomField = null;
        this.editingCustomFieldDraft = null;
        this.isNewCustomFieldDraft = false;
        this.editingCustomFieldIdError = null;
        this.editingCustomFieldLabelError = null;
        this.editingCustomFieldDescriptionError = null;
        this.editingCustomFieldTypeError = null;
        this.editingCustomFieldOptionsError = null;
    };

    @action
    public setEditingCustomFieldId = (id: string): void => {
        if (!this.editingCustomFieldDraft) {
            return;
        }

        this.editingCustomFieldDraft.fieldId = id;
        this.editingCustomFieldIdError = this.validateCustomFieldId(id);
    };

    @action
    public setEditingCustomFieldLabel = (label: string): void => {
        if (!this.editingCustomFieldDraft) {
            return;
        }

        this.editingCustomFieldDraft.label = label;
        this.editingCustomFieldLabelError = this.validateCustomFieldLabel(label, this.editingCustomFieldDraft.type);
    };

    @action
    public setEditingCustomFieldDescription = (description: string): void => {
        if (!this.editingCustomFieldDraft) {
            return;
        }

        this.editingCustomFieldDraft.description = description;
        this.editingCustomFieldDescriptionError = this.validateCustomFieldDescription(description);
    };

    @action
    public setEditingCustomFieldType = (type: HubCustomFieldType): void => {
        if (!this.editingCustomFieldDraft) {
            return;
        }

        this.editingCustomFieldDraft.type = type;
        this.editingCustomFieldTypeError = this.validateCustomFieldType(type);
        this.revalidateEditingCustomFieldLabel();

        if (type === HubCustomFieldType.Dropdown) {
            this.editingCustomFieldDraft.optionsList = this.editingCustomFieldDraft.optionsList ?? [];
        } else {
            this.editingCustomFieldDraft.optionsList = [];
        }
        this.editingCustomFieldOptionsError = null;
    };

    @action
    public setEditingCustomFieldOptions = (text: string): void => {
        if (!this.editingCustomFieldDraft) {
            return;
        }

        const options = text.split('\n');
        this.editingCustomFieldDraft.optionsList = options;
        this.editingCustomFieldOptionsError = this.validateCustomFieldOptions(this.editingCustomFieldDraft.type, options);
    };

    @action
    public toggleEditingCustomFieldVisibility = (): void => {
        if (!this.editingCustomFieldDraft) {
            return;
        }

        const configuration = ensureCustomFieldConfiguration(this.editingCustomFieldDraft);
        configuration.visible = !configuration.visible;

        if (!configuration.visible) {
            configuration.mandatory = false;
        }
    };

    @action
    public toggleEditingCustomFieldMandatory = (): void => {
        if (!this.editingCustomFieldDraft) {
            return;
        }

        const configuration = ensureCustomFieldConfiguration(this.editingCustomFieldDraft);
        if (!configuration.visible) {
            return;
        }

        configuration.mandatory = !configuration.mandatory;
    };

    @action
    public applyCustomFieldEditor = (): boolean => {
        if (!this.editingCustomFieldDraft) {
            return false;
        }

        const idError = this.validateCustomFieldId(this.editingCustomFieldDraft.fieldId);
        const labelError = this.validateCustomFieldLabel(
            this.editingCustomFieldDraft.label,
            this.editingCustomFieldDraft.type
        );
        const descriptionError = this.validateCustomFieldDescription(this.editingCustomFieldDraft.description || '');
        const typeError = this.validateCustomFieldType(this.editingCustomFieldDraft.type);
        const optionsError = this.validateCustomFieldOptions(
            this.editingCustomFieldDraft.type,
            this.editingCustomFieldDraft.optionsList
        );

        if (idError || labelError || descriptionError || typeError || optionsError) {
            this.editingCustomFieldIdError = idError;
            this.editingCustomFieldLabelError = labelError;
            this.editingCustomFieldDescriptionError = descriptionError;
            this.editingCustomFieldTypeError = typeError;
            this.editingCustomFieldOptionsError = optionsError;
            return false;
        }

        const wasVisible = this.editingCustomField ? getCustomFieldConfiguration(this.editingCustomField).visible : false;
        const isVisible = getCustomFieldConfiguration(this.editingCustomFieldDraft).visible;
        const isDropdown = this.editingCustomFieldDraft.type === HubCustomFieldType.Dropdown;

        const updatedField: IHubCustomField = {
            ...this.editingCustomFieldDraft,
            fieldId: this.editingCustomFieldDraft.fieldId.trim(),
            label: this.editingCustomFieldDraft.label.trim(),
            description: this.editingCustomFieldDraft.description?.trim() || undefined,
            configurations: this.editingCustomFieldDraft.configurations.map(configuration => ({ ...configuration })),
            optionsList: isDropdown
                ? (this.editingCustomFieldDraft.optionsList ?? []).map(option => option.trim()).filter(option => option.length > 0)
                : undefined,
        };

        if (wasVisible !== isVisible) {
            ensureCustomFieldConfiguration(updatedField).order = Number.MAX_SAFE_INTEGER;
        }

        const isNewField = updatedField.customFieldId < 0;

        if (isNewField) {
            this.customFields = dedupeCustomFieldsById([...this.customFields, updatedField]);
        } else {
            this.customFields = dedupeCustomFieldsById(this.customFields.map(field =>
                field.customFieldId === updatedField.customFieldId ? updatedField : field
            ));
        }

        if (wasVisible !== isVisible) {
            this.recalculateAllFieldOrders();
        }

        this.closeCustomFieldEditor();
        return true;
    };

    @action
    public deleteCustomField = (field: IHubCustomField): void => {
        if (field.customFieldId < 0) {
            this.customFields = this.customFields.filter(f => f.customFieldId !== field.customFieldId);
            this.closeCustomFieldEditor();
        } else {
            this.fieldToDelete = field;
            this.showDeleteConfirmation = true;
        }
    };

    @action
    public confirmDeleteCustomField = (): void => {
        if (!this.fieldToDelete) {
            return;
        }

        void this.performDeleteCustomField(this.fieldToDelete);
        this.showDeleteConfirmation = false;
        this.fieldToDelete = null;
        this.closeCustomFieldEditor();
    };

    @action
    public cancelDeleteCustomField = (): void => {
        this.showDeleteConfirmation = false;
        this.fieldToDelete = null;
    };

    public isShowingDeleteConfirmation(): boolean {
        return this.showDeleteConfirmation;
    }

    public getFieldToDelete(): IHubCustomField | null {
        return this.fieldToDelete;
    }

    @action
    public onCancel = (): void => {
        this.registrationEnabled = this.registrationEnabledSnapshot;
        this.registrationApprovalEnabled = this.registrationApprovalEnabledSnapshot;
        this.oneStepRegistrationEnabled = this.oneStepRegistrationEnabledSnapshot;
        this.displayName = this.displayNameSnapshot;
        this.instructions = this.instructionsSnapshot;
        this.registerButtonLabel = this.registerButtonLabelSnapshot;
        this.submittedMessage = this.submittedMessageSnapshot;
        this.registerButtonLabelTouched = false;
        this.submittedMessageTouched = false;
        this.fields = cloneFields(this.fieldsSnapshot);
        this.customFields = cloneCustomFields(this.customFieldsSnapshot);
        this.closeFieldEditor();
        this.closeCustomFieldEditor();
    };

    public onSaveChanges = (): void => {
        void this.saveSettings();
    };

    private getOrderedFields(): IHubRegistrationField[] {
        return [...this.fields].sort(
            (left, right) => getFieldConfiguration(left).order - getFieldConfiguration(right).order
        );
    }

    private getOrderedCustomFields(): IHubCustomField[] {
        return [...this.customFields].sort(
            (left, right) => getCustomFieldConfiguration(left).order - getCustomFieldConfiguration(right).order
        );
    }

    private recalculateAllFieldOrders(): void {
        const normalized = normalizeFieldOrders(this.fields, this.customFields);
        this.fields = normalized.fields;
        this.customFields = normalized.customFields;
    }

    private validateFieldLabel(label: string): string | null {
        const trimmedLabel = label.trim();

        if (!trimmedLabel) {
            return this.getRequiredFieldError(HUB_FIELD_LABEL_TEXT);
        }

        if (trimmedLabel.length > SHORT_PLAIN_TEXT_LENGTH) {
            return MAX_LENGTH_NOT_REQUIRED_ERROR_MESSAGE;
        }

        return null;
    }

    private getRequiredFieldError(fieldNameMessage: MessageDescriptor): string {
        const fieldName = this.intl.formatMessage(fieldNameMessage);
        return FIELD_IS_REQUIRED_ERROR_MESSAGE(fieldName, this.intl);
    }

    private validateRequiredCustomFieldText(value: string, fieldNameMessage: MessageDescriptor): string | null {
        if (!value.trim()) {
            return this.getRequiredFieldError(fieldNameMessage);
        }

        return null;
    }

    private validateCustomFieldMaxLength(value: string): string | null {
        if (value.trim().length > SHORT_PLAIN_TEXT_LENGTH) {
            return MAX_LENGTH_NOT_REQUIRED_ERROR_MESSAGE;
        }

        return null;
    }

    private validateCustomFieldIdUniqueness(id: string): string | null {
        const trimmedId = id.trim().toLowerCase();
        const isDuplicate = this.customFields.some(field =>
            field.fieldId.trim().toLowerCase() === trimmedId
            && field.customFieldId !== this.editingCustomField?.customFieldId
        );

        if (isDuplicate) {
            return this.intl.formatMessage(HUB_CUSTOM_FIELD_ID_ALREADY_EXISTS_TEXT);
        }

        return null;
    }

    private validateCustomFieldId(id: string): string | null {
        return this.validateRequiredCustomFieldText(id, HUB_FIELD_ID_TEXT)
            || this.validateCustomFieldMaxLength(id)
            || this.validateCustomFieldIdUniqueness(id);
    }

    private validateCustomFieldLabel(label: string, type: HubCustomFieldType): string | null {
        if (this.isRichTextLabelType(type)) {
            return this.validateRichTextCustomFieldLabel(label);
        }

        return this.validatePlainTextCustomFieldLabel(label);
    }

    private validatePlainTextCustomFieldLabel(label: string): string | null {
        return this.validateRequiredCustomFieldText(label, HUB_FIELD_LABEL_TEXT)
            || this.validateCustomFieldMaxLength(label);
    }

    private validateRichTextCustomFieldLabel(label: string): string | null {
        if (isRichTextEmpty(label)) {
            return this.getRequiredFieldError(HUB_FIELD_LABEL_TEXT);
        }
        if (label.trim().length > SHORT_RICH_TEXT_LENGTH) {
            return MAX_SHORT_RICH_TEXT_LENGTH_ERROR_MESSAGE;
        }

        return null;
    }

    private revalidateEditingCustomFieldLabel(): void {
        const draft = this.editingCustomFieldDraft;
        if (!draft?.label) {
            return;
        }

        this.editingCustomFieldLabelError = this.validateCustomFieldLabel(draft.label, draft.type);
    }

    private validateCustomFieldDescription(description: string): string | null {
        return this.validateCustomFieldMaxLength(description);
    }

    private validateCustomFieldType(type: HubCustomFieldType): string | null {
        if (!type) {
            return this.getRequiredFieldError(HUB_FIELD_TYPE_TEXT);
        }

        return null;
    }

    private validateCustomFieldOptions(type: HubCustomFieldType, optionsList?: string[]): string | null {
        if (type !== HubCustomFieldType.Dropdown) {
            return null;
        }

        const trimmedOptions = (optionsList ?? []).map(option => option.trim()).filter(option => option.length > 0);

        if (!trimmedOptions.length) {
            return this.getRequiredFieldError(HUB_FIELD_OPTIONS_TEXT);
        }
        if (trimmedOptions.length > MAX_CUSTOM_FIELD_OPTIONS_COUNT) {
            return MAX_OPTIONS_COUNT_ERROR_MESSAGE;
        }
        if (trimmedOptions.some(option => option.length > SHORT_PLAIN_TEXT_LENGTH)) {
            return MAX_OPTION_LENGTH_ERROR_MESSAGE;
        }

        return null;
    }

    @action
    private applyAccountRegistrationApprovalEnabled = (enabled: boolean): void => {
        this.accountRegistrationApprovalEnabled = enabled;
    };

    @action
    private setLoading = (loading: boolean): void => {
        this.loading = loading;
    };

    @action
    private setSaving = (saving: boolean): void => {
        this.saving = saving;
    };

    @action
    private applyLoadedState = (
        registrationSettings: IHubRegistrationDTO | undefined,
        fields: IHubRegistrationField[],
        customFields: IHubCustomField[] = []
    ): void => {
        const registrationEnabled = registrationSettings?.registrationEnabled ?? false;
        const registrationApprovalEnabled = registrationSettings?.registrationApprovalEnabled ?? false;
        const oneStepRegistrationEnabled = registrationSettings?.oneStepRegistrationEnabled ?? false;
        const displayName = registrationSettings?.registrationDisplayName ?? '';
        const instructions = transpileInlineStylesToClasses(registrationSettings?.registrationInstructions ?? '');
        const registerButtonLabel = registrationSettings?.registerButtonLabel || DEFAULT_REGISTER_BUTTON_LABEL;
        const submittedMessage = transpileInlineStylesToClasses(
            registrationSettings?.registrationSubmittedMessage || DEFAULT_REGISTRATION_SUBMITTED_MESSAGE
        );

        this.registrationEnabled = registrationEnabled;
        this.registrationApprovalEnabled = registrationApprovalEnabled;
        this.oneStepRegistrationEnabled = oneStepRegistrationEnabled;
        this.displayName = displayName;
        this.instructions = instructions;
        this.registerButtonLabel = registerButtonLabel;
        this.submittedMessage = submittedMessage;
        this.registerButtonLabelTouched = false;
        this.submittedMessageTouched = false;
        const normalized = normalizeFieldOrders(cloneFields(fields), cloneCustomFields(customFields));
        this.fields = normalized.fields;
        this.customFields = normalized.customFields;

        this.registrationEnabledSnapshot = registrationEnabled;
        this.registrationApprovalEnabledSnapshot = registrationApprovalEnabled;
        this.oneStepRegistrationEnabledSnapshot = oneStepRegistrationEnabled;
        this.displayNameSnapshot = displayName;
        this.instructionsSnapshot = instructions;
        this.registerButtonLabelSnapshot = registerButtonLabel;
        this.submittedMessageSnapshot = submittedMessage;
        this.fieldsSnapshot = cloneFields(fields);
        this.customFieldsSnapshot = cloneCustomFields(customFields);
    };

    @action
    private commitSavedState = (fields: IHubRegistrationField[], customFields: IHubCustomField[] = []): void => {
        this.registerButtonLabelTouched = false;
        this.submittedMessageTouched = false;
        this.fields = cloneFields(fields);
        this.customFields = cloneCustomFields(customFields);

        this.registrationEnabledSnapshot = this.registrationEnabled;
        this.registrationApprovalEnabledSnapshot = this.registrationApprovalEnabled;
        this.oneStepRegistrationEnabledSnapshot = this.oneStepRegistrationEnabled;
        this.displayNameSnapshot = this.displayName;
        this.instructionsSnapshot = this.instructions;
        this.registerButtonLabelSnapshot = this.registerButtonLabel;
        this.submittedMessageSnapshot = this.submittedMessage;
        this.fieldsSnapshot = cloneFields(fields);
        this.customFieldsSnapshot = cloneCustomFields(customFields);
    };

    private async loadSettings(showLoading = true): Promise<void> {
        if (showLoading) {
            this.setLoading(true);
        }
        try {
            const [configs, formConfiguration, eventHub] = await Promise.all([
                this.eventHubsDAO.getEventHubConfigs(this.accountId, this.eventHubId),
                this.hubRegistrationConfigurationDAO.getRegistrationFormConfiguration(
                    this.accountId,
                    this.eventHubId
                ),
                this.eventHubsDAO.getEventHub(this.accountId, this.eventHubId),
            ]);

            const registrationSettings = configs.find(
                config => config.type === HubConfigurationSettingsType.REGISTRATION
            ) as IHubRegistrationDTO | undefined;

            const { defaultFields, customFields } = splitWireFields(formConfiguration.fields as unknown[]);
            const editorCustomFields = this.toEditorCustomFields(customFields);
            this.applyAccountRegistrationApprovalEnabled(!!eventHub.registrationApprovalEnabled);
            this.applyLoadedState(registrationSettings, defaultFields, editorCustomFields);
        } catch (error) {
            this.handleHubApiException(error);
        } finally {
            if (showLoading) {
                this.setLoading(false);
            }
        }
    }

    private getInvalidFieldLabelError(): string | null {
        for (const field of this.fields) {
            const labelError = this.validateFieldLabel(field.label);
            if (labelError) {
                return labelError;
            }
        }

        return null;
    }

    private async saveSettings(): Promise<void> {
        if (this.loading || this.saving || !this.hasUnsavedChanges()) {
            return;
        }

        this.markRegisterButtonLabelTouched();
        this.markSubmittedMessageTouched();
        if (
            this.getDisplayNameError()
            || this.getInstructionsError()
            || this.getRegisterButtonLabelError()
            || this.getSubmittedMessageError()
        ) {
            return;
        }

        const invalidFieldLabelError = this.getInvalidFieldLabelError();
        if (invalidFieldLabelError) {
            alertServerError('Error', invalidFieldLabelError);
            return;
        }

        this.setSaving(true);
        const refreshGeneration = this.onHubPreviewRefresh?.beginLoading();
        try {
            const registrationPayload: IHubRegistrationDTO = {
                accountId: this.accountId,
                eventHubId: this.eventHubId,
                type: HubConfigurationSettingsType.REGISTRATION,
                registrationEnabled: this.registrationEnabled,
                registrationApprovalEnabled: this.registrationApprovalEnabled,
                oneStepRegistrationEnabled: this.oneStepRegistrationEnabled,
                registrationDisplayName: this.displayName.trim() || null,
                registrationInstructions: isRichTextEmpty(this.instructions)
                    ? null
                    : transpileClassesToInlineStyles(this.instructions),
                registerButtonLabel: this.registerButtonLabel.trim(),
                registrationSubmittedMessage: transpileClassesToInlineStyles(this.submittedMessage),
            };

            const payloadCustomFields = this.toPayloadCustomFields(this.customFields);
            const formPayload = buildFormPayload(this.fields, payloadCustomFields);

            await this.eventHubsDAO.setEventHubConfig(registrationPayload);
            const updatedForm = await this.hubRegistrationConfigurationDAO.updateRegistrationFormConfiguration(
                this.accountId,
                this.eventHubId,
                formPayload
            );

            const { defaultFields, customFields } = splitWireFields(updatedForm.fields as unknown[]);
            const editorCustomFields = this.toEditorCustomFields(customFields);
            this.commitSavedState(defaultFields, editorCustomFields);
            this.trackingService.get().track(EVENT_HUB_TRACKING_EVENTS.CONFIGURE_HUB_REGISTRATION, {
                'Registration Enabled': this.registrationEnabled,
                'Registration Approval Enabled': this.registrationApprovalEnabled,
            });
            void this.onHubPreviewRefresh?.refresh(
                buildRegistrationPreviewExpectation(registrationPayload, defaultFields),
                refreshGeneration,
            );
        } catch (error) {
            if (refreshGeneration != null) {
                this.onHubPreviewRefresh?.cancelLoading(refreshGeneration);
            }
            this.handleHubApiException(error);
            await this.loadSettings(false);
        } finally {
            this.setSaving(false);
        }
    }

    private async performDeleteCustomField(field: IHubCustomField): Promise<void> {
        try {
            await this.hubRegistrationConfigurationDAO.deleteCustomField(
                this.accountId,
                this.eventHubId,
                field.customFieldId
            );
            this.removeCustomFieldFromState(field);
        } catch (error) {
            this.handleHubApiException(error);
        }
    }

    @action
    private removeCustomFieldFromState = (field: IHubCustomField): void => {
        this.customFields = this.customFields.filter(f => f.customFieldId !== field.customFieldId);
        this.customFieldsSnapshot = this.customFields.map(f => ({
            ...f,
            configurations: f.configurations.map(c => ({ ...c })),
        }));
    };

    private handleHubApiException(error: Error): void {
        const errorParsingService = Container.get(IErrorParsing);
        const apiError = errorParsingService.extractAPIError(error);
        const title = apiError?.errorCode === 403 ? 'Access Denied' : 'Error';
        alertServerError(title, errorParsingService.extractMessage(error));
    }

    private toEditorCustomFields(customFields: IHubCustomField[]): IHubCustomField[] {
        return customFields.map(field => ({
            ...field,
            label: this.toEditorCustomFieldLabel(field.label, field.type),
        }));
    }

    private toPayloadCustomFields(customFields: IHubCustomField[]): IHubCustomField[] {
        return customFields.map(field => ({
            ...field,
            label: this.toPayloadCustomFieldLabel(field.label, field.type),
        }));
    }

    private toEditorCustomFieldLabel(label: string, type: HubCustomFieldType): string {
        return this.isRichTextLabelType(type) ? transpileInlineStylesToClasses(label) : label;
    }

    private toPayloadCustomFieldLabel(label: string, type: HubCustomFieldType): string {
        return this.isRichTextLabelType(type) ? transpileClassesToInlineStyles(label) : label;
    }
}
